import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { formatCurrency, formatDateShort } from '@/utils/format';

function GuestDashboard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    guest_name: '',
    guest_phone: '',
    check_in: '',
    check_out: '',
    num_guests: 1,
    special_requests: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();

  const guestEmail = localStorage.getItem('guest_email');
  const bookingId = localStorage.getItem('guest_booking_id');

  useEffect(() => {
    if (!guestEmail || !bookingId) {
      navigate('/guest/login');
      return;
    }
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/guest/bookings`, {
        params: {
          guest_email: guestEmail,
          booking_id: bookingId
        }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('guest_email');
    localStorage.removeItem('guest_booking_id');
    navigate('/guest/login');
  };

  const handleCancelBooking = async (bookingToCancel) => {
    try {
      await axios.delete(`${API}/guest/booking/${bookingToCancel.id}`, {
        params: {
          guest_email: guestEmail
        }
      });
      toast.success('Booking cancelled successfully');
      setShowCancelDialog(false);
      setSelectedBooking(null);
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Error cancelling booking:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to cancel booking');
      }
    }
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    // Pre-populate form with current booking data
    setEditForm({
      guest_name: booking.guest_name || '',
      guest_phone: booking.guest_phone || '',
      check_in: booking.check_in,
      check_out: booking.check_out,
      num_guests: booking.num_guests,
      special_requests: booking.special_requests || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveBookingChanges = async () => {
    if (!selectedBooking) return;
    
    setSaving(true);
    try {
      const updateData = {
        room_ids: selectedBooking.room_ids || [selectedBooking.room_id],
        guest_name: editForm.guest_name,
        guest_phone: editForm.guest_phone,
        guest_email: guestEmail,
        check_in: editForm.check_in,
        check_out: editForm.check_out,
        num_guests: editForm.num_guests,
        special_requests: editForm.special_requests
      };

      await axios.put(`${API}/guest/booking/${selectedBooking.id}`, updateData, {
        params: {
          guest_email: guestEmail
        }
      });

      toast.success('✅ Booking updated successfully!');
      setShowEditDialog(false);
      setSelectedBooking(null);
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Error updating booking:', error);
      
      // Enhanced error handling for availability issues
      if (error.response?.data?.detail) {
        const errorMessage = error.response.data.detail;
        
        // Special handling for availability errors
        if (errorMessage.includes('Rooms not available')) {
          toast.error(`❌ Sorry! Your rooms are not available for the selected dates.\n\n${errorMessage}\n\nPlease choose different dates or contact the hotel directly.`, {
            duration: 6000
          });
        } else if (errorMessage.includes('Cannot modify past bookings')) {
          toast.error('❌ Past bookings cannot be modified. Please contact the hotel for assistance.');
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to update booking. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canModifyBooking = (booking) => {
    const checkInDate = new Date(booking.check_in);
    const today = new Date();
    return checkInDate > today && booking.payment_status !== 'cancelled';
  };

  const needsPaymentCompletion = (booking) => {
    // Can complete payment if:
    // 1. Status is "pending_payment" (pay_on_arrival bookings)
    // 2. Status is "pending" with remaining balance (flexible bookings with only deposit paid)
    // 3. Not cancelled and check-in date is in the future
    const checkInDate = new Date(booking.check_in);
    const today = new Date();
    
    if (checkInDate <= today || booking.payment_status === 'cancelled' || booking.payment_status === 'paid') {
      return false;
    }
    
    return booking.payment_status === 'pending_payment' || 
           (booking.payment_status === 'pending' && booking.deposit_amount && booking.deposit_amount < booking.total_amount);
  };

  const getRemainingAmount = (booking) => {
    if (booking.payment_status === 'pending_payment') {
      // Pay on arrival - full amount due
      return booking.total_amount;
    } else if (booking.deposit_amount) {
      // Flexible booking - remaining balance
      return booking.total_amount - booking.deposit_amount;
    }
    return booking.total_amount;
  };

  const handleCompletePayment = async (booking) => {
    setSelectedBooking(booking);
    setProcessingPayment(true);
    
    try {
      const remainingAmount = getRemainingAmount(booking);
      
      // Create Stripe checkout session for remaining payment
      const response = await axios.post(`${API}/guest/booking/${booking.id}/complete-payment`, {
        guest_email: guestEmail,
        amount: remainingAmount
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Unable to process payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
    } finally {
      setProcessingPayment(false);
      setSelectedBooking(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scrollable-page bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
              <p className="text-gray-600 mt-1">{guestEmail}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Bookings List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {bookings.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">No bookings found</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {bookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">
                        Booking #{booking.id.slice(0, 8)}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.payment_status)}`}>
                        {booking.payment_status}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 text-gray-600">
                      <div>
                        <p className="text-sm text-gray-500">Check-in</p>
                        <p className="font-medium">{formatDateShort(booking.check_in)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Check-out</p>
                        <p className="font-medium">{formatDateShort(booking.check_out)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Guests</p>
                        <p className="font-medium">{booking.num_guests}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Amount</p>
                        <p className="font-medium text-green-600">{formatCurrency(booking.total_amount)}</p>
                      </div>
                    </div>

                    {/* Enhanced Payment Information */}
                    {booking.deposit_amount && booking.deposit_amount > 0 && (
                      <div className={`border rounded p-3 mt-4 ${
                        booking.payment_status === 'paid' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-yellow-50 border-yellow-200'
                      }`}>
                        <div className="flex justify-between items-center text-sm">
                          <span className={booking.payment_status === 'paid' ? 'text-green-700' : 'text-yellow-700'}>
                            {booking.payment_status === 'paid' ? '✓ Deposit Paid' : '⏳ Deposit Required'}
                          </span>
                          <span className={`font-medium ${booking.payment_status === 'paid' ? 'text-green-800' : 'text-yellow-800'}`}>
                            {formatCurrency(booking.deposit_amount)}
                          </span>
                        </div>
                        {booking.deposit_amount < booking.total_amount && (
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="text-orange-700">Remaining Balance (due at check-in)</span>
                            <span className="font-medium text-orange-800">{formatCurrency(booking.total_amount - booking.deposit_amount)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {booking.payment_status === 'pending' && !booking.deposit_amount && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
                        <p className="text-sm text-yellow-800">
                          <strong>⏳ Awaiting Payment:</strong> Please complete your payment to confirm this booking.
                        </p>
                      </div>
                    )}
                    
                    {booking.payment_status === 'pending_payment' && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4">
                        <p className="text-sm text-amber-800">
                          <strong>Pay on Arrival:</strong> Full payment due at check-in
                        </p>
                      </div>
                    )}

                    {booking.rooms && booking.rooms.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Rooms:</p>
                        <div className="flex flex-wrap gap-2">
                          {booking.rooms.map((room, idx) => (
                            <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                              {room.name || room.room_number}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add-ons section */}
                    {(booking.car_id || booking.transfer_required) && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Add-ons:</p>
                        <div className="flex flex-wrap gap-2">
                          {booking.car_id && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-1">
                              <span>🚗</span> Car Rental
                            </span>
                          )}
                          {booking.transfer_required && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center gap-1">
                              <span>✈️</span> Airport Transfer ({booking.transfer_type || 'N/A'})
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {booking.special_requests && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Special Requests:</p>
                        <p className="text-gray-700">{booking.special_requests}</p>
                      </div>
                    )}
                  </div>

                  {canModifyBooking(booking) && (
                    <div className="ml-4 flex flex-col gap-2">
                      {needsPaymentCompletion(booking) && (
                        <button
                          onClick={() => handleCompletePayment(booking)}
                          disabled={processingPayment && selectedBooking?.id === booking.id}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {processingPayment && selectedBooking?.id === booking.id ? 'Processing...' : 
                            `Pay ${formatCurrency(getRemainingAmount(booking))}`
                          }
                        </button>
                      )}
                      <button
                        onClick={() => handleEditBooking(booking)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Modify
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowCancelDialog(true);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Cancel Booking?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your booking for{' '}
              {formatDateShort(selectedBooking.check_in)} - {formatDateShort(selectedBooking.check_out)}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelDialog(false);
                  setSelectedBooking(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Keep Booking
              </button>
              <button
                onClick={() => handleCancelBooking(selectedBooking)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Dialog */}
      {showEditDialog && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Modify Booking #{selectedBooking.id.slice(0, 8)}</h3>
            
            <div className="space-y-4">
              {/* Guest Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name</label>
                  <input
                    type="text"
                    value={editForm.guest_name}
                    onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editForm.guest_phone}
                    onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Check-in/Check-out Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                  <input
                    type="date"
                    value={editForm.check_in}
                    onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                  <input
                    type="date"
                    value={editForm.check_out}
                    onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                    min={editForm.check_in || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Number of Guests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
                <select
                  value={editForm.num_guests}
                  onChange={(e) => setEditForm({ ...editForm, num_guests: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                <textarea
                  value={editForm.special_requests}
                  onChange={(e) => setEditForm({ ...editForm, special_requests: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any special requests or requirements..."
                />
              </div>

              {/* Current Room Info (read-only) */}
              {selectedBooking.rooms && selectedBooking.rooms.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Current Rooms:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedBooking.rooms.map((room, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {room.name || room.room_number}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Note: To change rooms, please contact the hotel directly.
                  </p>
                </div>
              )}

              {/* Warning about date changes */}
              {(editForm.check_in !== selectedBooking.check_in || editForm.check_out !== selectedBooking.check_out) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>⚠️ Important:</strong> Changing dates will:
                  </p>
                  <ul className="text-xs text-amber-700 mt-1 ml-4 list-disc">
                    <li>Check if your current rooms are available for the new dates</li>
                    <li>Recalculate the total price based on current rates</li>
                    <li>If rooms aren't available, you'll be notified before any changes</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditDialog(false);
                  setSelectedBooking(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBookingChanges}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestDashboard;
